import { FC, useState } from 'react';
import { LazyMotion, domAnimation, m } from 'framer-motion';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import MobileToolTab from './mobile-tool-tab';
import MobileToolSheet from './mobile-tool-sheet';
import { BREAKPOINTS, FIXTURE_TICKER_PATH } from '@lib/constants';
import getWindowDimensions from '@hooks/useWindowSize';
import CaptaincyPoll from '@components/CaptaincyPoll';
import Button from '@components/atoms/button';
import { H2 } from '@components/atoms/typography';
import { DifficultyLegend, MiniFixtureTicker } from '@components/fixtures/v2';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';
import { usePostHogFeatureFlag } from '@hooks/usePostHogFeatureFlag';
import { useCaptaincyPollDefinition } from '@hooks/useCaptaincyPoll';
const RightSidebar = dynamic(() => import('@components/layout/sidebar/right'));

// Taller than the sidebar ticker's rows so each fixture is a comfortable
// target to read on a phone.
const SHEET_TICKER_ROW_HEIGHT = 32;

type LayoutType = {
  hasRightSidebar?: boolean;
  content?: boolean;
  children: React.ReactNode;
};

const Layout: FC<LayoutType> = ({
  hasRightSidebar = true,
  children,
  content = true
}) => {
  const { context: leagueContext } = useLeagueContext();
  const currentGameweek = leagueContext?.currentGameweek?.number ?? 0;
  const router = useRouter();
  const [openTool, setOpenTool] = useState<'poll' | 'fixture-ticker' | null>(
    null
  );
  const closeTool = () => setOpenTool(null);

  const fplSeasonIsClosed = usePostHogFeatureFlag(
    PostHogFeatureFlags.fplSeasonIsClosed,
    false
  );

  const showMobileTools =
    getWindowDimensions().width < BREAKPOINTS.lg &&
    router.pathname === '/' &&
    !fplSeasonIsClosed &&
    currentGameweek !== 38;

  const communityVoteIsLive = usePostHogFeatureFlag(
    PostHogFeatureFlags.communityVoteIsLive,
    false
  );

  const { status: captaincyPollStatus } = useCaptaincyPollDefinition({
    isEnabled: showMobileTools && communityVoteIsLive
  });

  return (
    <main>
      <LazyMotion features={domAnimation}>
        {/* @ts-ignore */}
        <m.div
          exit={{ opacity: 0 }}
          initial={{ opacity: 0.1 }}
          animate={{ opacity: 1 }}
        >
          <div className="container mx-auto px-3">
            <div
              className={`flex flex-col lg:grid ${
                hasRightSidebar
                  ? 'lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_325px]'
                  : ''
              } gap-8`}
            >
              <div>
                {showMobileTools && (
                  <div>
                    <H2 className="mt-5 mb-3">Community & tools</H2>
                    <div className="grid auto-cols-fr grid-flow-col gap-3">
                      {communityVoteIsLive &&
                        captaincyPollStatus !== 'hidden' && (
                          <MobileToolTab
                            icon="🗳️"
                            title="Captaincy poll"
                            subtitle={
                              captaincyPollStatus === 'open'
                                ? 'Who are you captaining?'
                                : 'See who everyone captained'
                            }
                            onClick={() => setOpenTool('poll')}
                          />
                        )}
                      <MobileToolTab
                        icon="📅"
                        title="Fixture ticker"
                        subtitle="Every team's next three gameweeks"
                        onClick={() => setOpenTool('fixture-ticker')}
                      />
                    </div>
                  </div>
                )}
                <section className={`${content ? 'content' : ''}`}>
                  {children}
                </section>
              </div>
              {hasRightSidebar && (
                <div className="hidden lg:block">
                  <RightSidebar />
                </div>
              )}
            </div>
          </div>
        </m.div>
      </LazyMotion>
      <MobileToolSheet
        isOpen={openTool === 'poll'}
        onClose={closeTool}
        title="Captaincy poll"
      >
        <CaptaincyPoll placement="mobile_modal" className="pt-4" />
      </MobileToolSheet>
      <MobileToolSheet
        isOpen={openTool === 'fixture-ticker'}
        onClose={closeTool}
        title="Fixture ticker"
        subtitle="Every team's next three gameweeks by difficulty"
        footer={
          <div className="flex items-center justify-between gap-3">
            <DifficultyLegend />
            <Button
              variant="primary"
              size="small"
              href={FIXTURE_TICKER_PATH}
              isLink
            >
              Full ticker
            </Button>
          </div>
        }
      >
        <MiniFixtureTicker
          rowHeight={SHEET_TICKER_ROW_HEIGHT}
          hasStickyHeader
        />
      </MobileToolSheet>
    </main>
  );
};

export default Layout;
